
// Import the reusable How It Works hook
import { useHowItWorks } from "./hooks/useHowItWorks";

// Import the reusable How It Works component
import { HowItWorks } from "./components/HowItWorks";

// Import the landing header
import { LandingHeader } from "./components/LandingHeader";
import { LandingFooter } from "./components/LandingFooter";
import { HeroCarousel } from "./components/HeroCarousel";
import { SaleProcess } from "./components/SaleProcess";
import { FaqSection } from "./components/FaqSection";

// Import the page stylesheet.
import "./LandingPage.css";



// Export the main landing page.
const LandingPage = () => {


    // Get the reusable How It Works controls
    const {
        activeJourney,
        howItWorksRef,
        selectJourney,
    } = useHowItWorks("inline");



    // Hardcode the main navigation links for now.
    const landingLinks = {

        // Link to the existing sign-in page.
        signIn: "/login",

        // Link to the account registration page.
        createAccount: "/register",

    };



    // Hardcode the carousel slides for now.
    const landingSlides = [

        // Build Your Brand slide.
        {
            // Give the slide a stable identifier.
            id: "build-your-brand",

            // Set the main slide heading.
            //title: "Build your brand",

            title: (

                <>
                    <br />
                    Build your brand

                </>

            ),

            // Set the supporting marketing text.
            description: "Design products, build your storefront, and let us take care of the rest. No set up fees or upfront payment.",

            // Set the image used on the slide.
            image: "/images/landing/build-your-brand.png",

            // Provide accessible alternative text for the image.
            imageAlt: "Creating and selling branded products with StrichSuite",

            // Set the background colour for this slide.
            backgroundColor: "#b1cee5",

            // Set the ID of the element to scroll to on 'see more' click
            scrollToId: "how-it-works",

            // Set the journey key so it shows the 'how it works' journey
            journeyKey: "store",

            // Set the destination for the details button.
            detailsUrl: "/features/build-your-brand",
            
        },

        // Existing Website slide.
        {
            // Give the slide a stable identifier.
            id: "existing-website",

            // Set the main slide heading.
            title: "Sell products on your existing website",

            // Set the supporting marketing text.
            description: "Integrate selling products on your existing websites with a couple of lines of code.",

            // Set the image used on the slide.
            image: "/images/landing/inline-products.png",

            // Provide accessible alternative text for the image.
            imageAlt: "Selling StrichSuite products through an existing website",

            // Set the background colour for this slide.
            backgroundColor: "#a1bc7e",

            // Set the ID of the element to scroll to on 'see more' click
            scrollToId: "how-it-works",

            // Set the journey key so it shows the 'how it works' journey
            journeyKey: "inline",

            // Set the destination for the details button.
            detailsUrl: "/features/inline-products",
        },

        // Followers To Customers slide.
        {
            // Give the slide a stable identifier.
            id: "followers-to-customers",

            // Set the main slide heading.
            title: "Turn followers into customers",

            // Set the supporting marketing text.
            description: "Direct product links and a streamlined checkout make it incredibly easy for your audience to buy.",

            // Set the image used on the slide.
            image: "/images/landing/single-product-page-links.png",

            // Provide accessible alternative text for the image.
            imageAlt: "Selling merchandise directly to social media followers",

            // Set the background colour for this slide.
            backgroundColor: "#deb266",

            // Set the ID of the element to scroll to on 'see more' click
            scrollToId: "how-it-works",

            // Set the journey key so it shows the 'how it works' journey
            journeyKey: "links",

            // Set the destination for the details button.
            detailsUrl: "/features/single-product-page-links",
        },

        // API Integration slide.
        {
            // Give the slide a stable identifier.
            id: "api-integration",

            // Set the main slide heading.
            //title: "API integration",

            title: (

                <>
                    <br />
                    API integration

                </>

            ),

            // Set the supporting marketing text.
            description: "Already have an online shop? Use our API to automatically send and track orders for fulfilment.",

            // Set the image used on the slide.
            image: "/images/landing/api-integration.png",

            // Provide accessible alternative text for the image.
            imageAlt: "Connecting an existing ecommerce website to StrichSuite using the API",

            // Set the background colour for this slide.
            backgroundColor: "#a0d2bc",

            // Set the ID of the element to scroll to on 'see more' click
            scrollToId: "api-integration",

            // Set the destination for the details button.
            detailsUrl: "/features/api-integration",
        },

        // AI Merch slide.
        {
            // Give the slide a stable identifier.
            id: "create-merch-with-ai",

            // Set the main slide heading.
            //title: "Create merch with AI",

            title: (

                <>
                    <br />
                    Create merch with AI

                </>

            ),

            // Set the supporting marketing text.
            description: "Use Strich's AI tools to create print ready images and apply to products to create merchandise for your audience.",

            // Set the image used on the slide.
            image: "/images/landing/create-merch-with-ai.png",

            // Provide accessible alternative text for the image.
            imageAlt: "Creating merchandise designs using StrichSuite AI tools",

            // Set the background colour for this slide.
            backgroundColor: "#c2a1c4",

            // Set the ID of the element to scroll to on 'see more' click
            scrollToId: "AI-image-generation",

            // Set the destination for the details button.
            detailsUrl: "/features/ai-merch",
        },

    ];





    // Create the available How It Works journeys
    const howItWorksJourneys = {

        // Define the existing website journey
        inline: {

            // Define the selector icon
            selectorIcon: "website",

            // Define the selector heading
            selectorTitle: "I've got a website!",

            // Define the selector image
            selectorImage: "/images/how-it-works/choice-website.jpg",

            // Define accessible image text
            selectorImageAlt: "Existing website displaying products",

            // Define the selector description
            selectorDescription:
                "Add inline shopping to your website with just two lines of code.",

            // Define the journey heading
            subtitle: "Turn images on your website into buyable products.",

            // Define the journey explanation
            description:
                "You can tag your images on your store and we'll turn them into pod products for you.",

            // Keep your existing journey steps
            steps: [

                // Define the first inline journey step
                {
                    // Give the step a stable key
                    key: "inline-products",

                    // Define the image
                    image: "/images/landing/how-it-works/inline-products.png",

                    // Define the image alternative text
                    imageAlt: "StrichSuite product selection",

                    // Define the step explanation
                    text: "Choose the products you want to offer on your website",

                },

                // Define the second inline journey step
                {
                    // Give the step a stable key
                    key: "inline-website",

                    // Define the image
                    image: "/images/landing/how-it-works/inline-website.png",

                    // Define the image alternative text
                    imageAlt: "Existing website containing artwork",

                    // Define the step explanation
                    text: "Connect your website and tag your sellable images",

                },

                // Define the third inline journey step
                {
                    // Give the step a stable key
                    key: "inline-shop",

                    // Define the image
                    image: "/images/landing/how-it-works/inline-popup.png",

                    // Define the image alternative text
                    imageAlt: "Shop Now products displayed on an existing website",

                    // Define the step explanation
                    text: "Products are created featuring those images",

                },

                // Define the fourth inline journey step
                {
                    // Give the step a stable key
                    key: "inline-checkout",

                    // Define the image
                    image: "/images/landing/how-it-works/inline-checkout.png",

                    // Define the image alternative text
                    imageAlt: "Checkout displayed on an existing website",

                    // Define the step explanation
                    text: "Customers can purchase without leaving your site ",

                },

            ],

            // Define the live examples action
            secondaryAction: {

                // Define the supporting caption
                caption: "YOUR WEBSITE. YOUR STYLE. POWERED BY STRICHSUITE.",

                // Define the button label
                label: "View Live Examples",

                // Define the demo website URL - PUT THIS IN CONFIG
                href: "https://strichsuite.com/demo_sites",

                // Open the demo site in another tab
                openNewTab: true,

            },

        },



        // Define the audience journey
        links: {

            // Define the selector icon
            selectorIcon: "audience",

            // Define the selector heading
            selectorTitle: "I've got an audience!",

            // Define the selector image
            selectorImage: "/images/landing/how-it-works/choice-audience.jpg",

            // Define accessible image text
            selectorImageAlt: "Creator sharing products with their social media audience",

            // Define the selector description
            selectorDescription:
                "Create merch for your followers to buy with simple sales links.",
                
            // Define the journey heading
            subtitle: "Create instant merch that your followers will love.",

            // Define the journey description
            description:
                //"Create branded products in StrichSuite and share their unique links wherever your audience already follows you. Your followers get a simple product page and checkout, while StrichSuite handles everything after the sale.",
                "You can create branded merch and post popup sales links for your followers.",

            // Define the product link journey steps
            steps: [

                // Define the product creation step
                {
                    // Give the step a stable key
                    key: "links-create",

                    // Define the image
                    image: "/images/landing/how-it-works/link_design.png",

                    // Define alternative text
                    imageAlt: "Add your logos or designs in StrichSuite",

                    // Define the explanation
                    text: "Add your logos or designs through our portal",

                },

                // Define the product creation step
                {
                    // Give the step a stable key
                    key: "links-products",

                    // Define the image
                    image: "/images/landing/how-it-works/link-create-product.png",

                    // Define alternative text
                    imageAlt: "Creating a product in StrichSuite",

                    // Define the explanation
                    text: "Create the products you want to offer your audience ",

                },

                // Define the sharing step
                {
                    // Give the step a stable key
                    key: "links-share",

                    // Define the image
                    image: "/images/landing/how-it-works/link-social.png",

                    // Define alternative text
                    imageAlt: "Product shared through social media",

                    // Define the explanation
                    text: "Share the unique purchase links with your followers",

                },

                // Define the checkout step
                {
                    // Give the step a stable key
                    key: "links-checkout",

                    // Define the image
                    image: "/images/landing/how-it-works/link-checkout.png",

                    // Define alternative text
                    imageAlt: "Mobile product checkout completed",

                    // Define the explanation
                    text: "Customers can purchase on one simple page",

                },

            ],

        },



        // Define the hosted store journey
        store: {

            // Define the selector icon
            selectorIcon: "idea",

            // Define the selector heading
            selectorTitle: "I've got nothing...",

            // Define the playful subtitle
            //selectorSubtitle: "That's OK.",

            // Define the selector image
            selectorImage: "/images/landing/how-it-works/choice-idea.jpg",

            // Define accessible image text
            selectorImageAlt: "Creator starting a new product idea",

            // Define the selector description
            selectorDescription:
                //"You provide the idea. We'll help turn it into products and a store.",
                "No problem! You provide the ideas and we'll build you a store.",

            // Define the journey heading
            subtitle: "Start with an idea. Build the rest for free.",

            // Define the journey explanation
            description:
                //"Create or upload your designs, turn them into products and build your own StrichSuite store without needing a website, stock or upfront fulfilment costs.",
                "You provide the ideas and we'll make you a store. ",

            // Define the store journey steps
            steps: [

                // Define the design step
                {
                    // Give the step a stable key
                    key: "store-design",

                    // Define the image
                    image: "/images/landing/how-it-works/store-create-design.png",

                    // Define alternative text
                    imageAlt: "Creating artwork in StrichSuite",

                    // Define the explanation
                    text: "Upload your artwork or create something new with AI",

                },

                // Define the product step
                {
                    // Give the step a stable key
                    key: "store-products",

                    // Define the image
                    image: "/images/landing/how-it-works/store-products.png",

                    // Define alternative text
                    imageAlt: "Design applied to multiple products",

                    // Define the explanation
                    text: "Choose which products you want your new designs on",

                },

                // Define the storefront step
                {
                    // Give the step a stable key
                    key: "store-publish",

                    // Define the image
                    image: "/images/landing/how-it-works/store-publish.png",

                    // Define alternative text
                    imageAlt: "Branded StrichSuite storefront",

                    // Define the explanation
                    text: "Style your automatically created store the way you like",

                },

                // Define the storefront step
                {
                    // Give the step a stable key
                    key: "store-promote",

                    // Define the image
                    image: "/images/landing/how-it-works/store-promote.png",

                    // Define alternative text
                    imageAlt: "Branded StrichSuite storefront",

                    // Define the explanation
                    text: "Promote it and start selling",

                },

            ],

        },

    };


    /*
    // Hardcode the order process content for now.
    const _saleProcess = {

        // Set the section heading.
        title: "What Happens After a Customer Places an Order",

        // Set the supporting section text.
        subtitle: "We handle everything, so you can focus on creating.",

        // Define the individual order process steps.
        steps: [

            // Customer purchase step.
            {
                // Give the step a stable identifier.
                id: "customer-buys",

                // Select the local icon to display.
                icon: "basket",

                // Set the step heading.
                title: "Customer buys",

                // Explain what happens at this stage.
                description: "We handle payment processing and keep the checkout experience simple for your customer.",
            },

            // Fulfilment step.
            {
                // Give the step a stable identifier.
                id: "fulfil",

                // Select the local icon to display.
                icon: "printer",

                // Set the step heading.
                title: "We fulfil",

                // Explain what happens at this stage.
                description: "We print, prepare and pack the order ready for dispatch.",
            },

            // Tracking step.
            {
                // Give the step a stable identifier.
                id: "track-delivery",

                // Select the local icon to display.
                icon: "tracking",

                // Set the step heading.
                title: "Track delivery",

                // Explain what happens at this stage.
                description: "Delivery tracking keeps your customer informed as their order makes its way to them.",
            },

            // Earnings step.
            {
                // Give the step a stable identifier.
                id: "get-paid",

                // Select the local icon to display.
                icon: "payment",

                // Set the step heading.
                title: "Get paid",

                // Explain what happens at this stage.
                description: "Your earnings are calculated automatically and added to your account.",
            },

            // Customer support step.
            {
                // Give the step a stable identifier.
                id: "customer-support",

                // Select the local icon to display.
                icon: "support",

                // Set the step heading.
                title: "Deal with customer issues",

                // Explain what happens at this stage.
                description: "We help manage fulfilment and delivery issues so you do not have to handle everything yourself.",
            },

            // Creator focus step.
            {
                // Give the step a stable identifier.
                id: "focus-on-creating",

                // Select the local icon to display.
                icon: "shield",

                // Set the step heading.
                title: "You focus on what you do best",

                // Explain what happens at this stage.
                description: "Spend your time creating, marketing and growing your brand while we handle the fulfilment.",
            },

        ],

    };
    */

    // Hardcode the order process content for now.
    const saleProcess = {

        // Set the section heading.
        title: "What Happens After a Customer Places an Order",

        // Set the supporting section text.
        subtitle: "We handle everything, so you can focus on creating.",

        // Define the individual order process steps.
        steps: [

            // Customer purchase step.
            {
                // Give the step a stable identifier.
                id: "customer-buys",

                // Select the local icon to display.
                icon: "basket",

                // Set the step heading.
                title: "Customer buys",

                // Explain what happens at this stage.
                description: "You receive an order through your Strich store, existing website or product link.",
            },

            // Fulfilment step.
            {
                // Give the step a stable identifier.
                id: "fulfil",

                // Select the local icon to display.
                icon: "product",

                // Set the step heading.
                title: "We fulfil and ship",

                // Explain what happens at this stage.
                description: "We print, pack and dispatch the order straight to your customer.",
            },
           
            // Earnings step.
            {
                // Give the step a stable identifier.
                id: "get-paid",

                // Select the local icon to display.
                icon: "payment",

                // Set the step heading.
                title: "You get paid",

                // Explain what happens at this stage.
                description: "Your earnings are calculated automatically and added to your account.",
            },
            
        ],

        // Set the heading for the additional benefits.
        benefitsTitle: "We take care of the complicated parts too.",

        // Set the introduction for the additional benefits.
        //benefitsSubtitle: "We take care of the complicated parts of selling too.",

        // Define the additional services handled by StrichSuite.
        benefits: [

            // Explain the Merchant of Record service.
            {
                // Give the benefit a stable identifier.
                id: "merchant-of-record",

                // Set the benefit heading.
                title: "We're the Merchant of Record",

                // Explain what this means for the seller.
                description:
                    "StrichSuite handles the customer transaction, including the taxes, fees and duties that apply to their order.",

            },

            // Explain customer support.
            {
                // Give the benefit a stable identifier.
                id: "customer-support",

                // Set the benefit heading.
                title: "We handle customer service",

                // Explain the support provided.
                description:
                    "Any customer questions or issues with delivery are handled by our trained customer service team.",

            },

            // Explain worldwide delivery.
            {
                // Give the benefit a stable identifier.
                id: "worldwide-shipping",

                // Set the benefit heading.
                title: "Sell around the world",

                // Explain worldwide shipping.
                description:
                    "Reach customers internationally with worldwide shipping and delivery options calculated for their destination.",

            },

            // Explain currency handling.
            {
                // Give the benefit a stable identifier.
                id: "currencies",

                // Set the benefit heading.
                title: "Localised pricing",

                // Explain currency conversion.
                description:
                    "Pricing is automatically calculated, making sure you earn the same regardless of destination country, currency or taxes.",

            },

            // Explain order tracking.
            {
                // Give the benefit a stable identifier.
                id: "order-tracking",

                // Set the benefit heading.
                title: "Track every order",

                // Explain the order visibility.
                description:
                    "Follow orders through production, dispatch and delivery, with full shipment tracking available.",

            },

            // Explain the lack of upfront fulfilment costs.
            {
                // Give the benefit a stable identifier.
                id: "no-upfront-fulfilment",

                // Set the benefit heading.
                title: "No upfront costs",

                // Explain the print-on-demand model.
                description:
                    "You earn the difference between the sale price and the cost of fulfilment  - so you never have to pay anything up front.",

            },

        ],

    };



    // Hardcode some starter FAQ content.
    const landingFaqs = [

        // What is StrichSuite
        {
            // Give the FAQ a stable identifier.
            id: "StrichSuite",

            // Set the FAQ question.
            question: "What is StrichSuite?",

            // Set the FAQ answer.
            answer: (

                <>
                    StrichSuite is an all-in-one platform that lets you create, sell and fulfil custom products from a single place. Whether you're selling artwork, growing a business, or creating merchandise for your fans, community, or audience, StrichSuite gives you everything you need without the usual complexity.

                    <br />
                    <br />

                    Create designs, customise products, build your storefront, manage orders and ship worldwide — all from one platform.

                    <br />
                    <br />

                    No juggling multiple services. No switching between apps. No complicated setup.
                </>

            )
        },

        // Account pricing question.
        {
            // Give the FAQ a stable identifier.
            id: "cost",

            // Set the FAQ question.
            question: "How much does it cost?",

            // Set the FAQ answer.
            answer: (

                <>
                    StrichSuite is free to use.

                    <br />
                    <br />

                    There is no setup fee, no monthly commitment and no upfront fulfilment costs on the free plan. No free trials, just free.

                    <br />
                    <br />

                    You only pay for product fulfilment and platform fees when you've already made the sale, so there's no need to buy stock, invest in inventory or risk money before you've earned it.

                    <br />
                    <br />

                    It's a simple, low-risk way to start selling online.

                </>
                
            )
        },

        // Who is it for?
        {
            // Give the FAQ a stable identifier.
            id: "who",

            // Set the FAQ question.
            question: "Who is it for?",

            // Set the FAQ answer.
            answer: (

                <>
                    StrichSuite is built for anyone who wants to sell print-on-demand products online, including: 

                    <br />
                    <br />

                    <ul style={{marginLeft: "30px"}}>
                        <li>Artists and designers </li>
                        <li>Bands, musicians and performers </li> 
                        <li>Creators and influencers </li> 
                        <li>Sports clubs, charities and events </li> 
                        <li>Streamers, YouTubers and podcasters </li> 
                        <li>Businesses and organisations </li> 
                        <li>Clothing and lifestyle brands </li> 
                        <li>Anyone with an audience, idea or brand </li> 
                    </ul>

                    <br />
                 
                    Whether you're just getting started or already selling thousands of products, StrichSuite works for you.

                </>
                
            )
        },

        // Why choose StrichSuite?
        {
            // Give the FAQ a stable identifier.
            id: "why",

            // Set the FAQ question.
            question: "Why choose StrichSuite?",

            // Set the FAQ answer.
            answer: (

                <>
                    Most ecommerce businesses require multiple subscriptions and tools just to get started. With StrichSuite, you have everything you need in one place.

                    <br/>
                    <br/>

                    <ul style={{marginLeft: "30px"}}>
                        <li>Create products without expensive design software </li>
                        <li>Upload and edit your own artwork OR use Strich’s built-in AI image generator to create print ready designs </li> 
                        <li>Sell through your own fully branded online stores hosted by StrichSuite </li> 
                        <li>Sell products seamlessly through your existing website </li> 
                        <li>Automatic price calculations with clear profit values </li> 
                        <li>Print-on-demand with worldwide fulfilment </li> 
                        <li>Manage products, customers and orders in one dashboard </li> 
                        <li>No stock to hold. No packing orders. No fulfilment headaches </li> 
                    </ul>

                    <br />
                 
                    From your first design to your customer's front door, StrichSuite handles the entire journey. 

                </>
                
            )
        },

        // Account pricing question.
        {
            // Give the FAQ a stable identifier.
            id: "grow",

            // Set the FAQ question.
            question: "Will it grow with me?",

            // Set the FAQ answer.
            answer: (

                <>
                    From a free account, to our highest tier subscription, StrichSuite scales with you. As you grow you can unlock lower product prices, lower platform fees, faster fulfilment and faster AI workflows. You can upgrade or downscale as you please and choose whatever plan works best for you at that time. 

                </>
                
            )
        },

        // Inventory question.
        {
            // Give the FAQ a stable identifier.
            id: "inventory",

            // Set the FAQ question.
            question: "Do I need to buy stock upfront?",

            // Set the FAQ answer.
            answer: "Products are be produced and shipped when your customer places an order; so you do not need to purchase or hold any stock yourself.",
        },

        // Website question.
        {
            // Give the FAQ a stable identifier.
            id: "website",

            // Set the FAQ question.
            question: "Can I use StrichSuite with my existing website?",

            // Set the FAQ answer.
            answer: "StrichSuite can plug seamlessly into your existing website with just a couple of easy to apply lines of code and you can style StrichSuite elements like the cart and checkout to match your website's look and feel perfectly. Customers make their purchases directly on your site with no dilution of your brand.",
        },

    ];



    // Create the AI feature selling points
    const aiFeatures = [

        // Define the AI model feature
        {
            // Give the feature a stable key
            key: "format",

            // Define the simple icon
            icon: "▣",

            // Define the feature title
            title: "Choose the format",

            // Define the feature description
            description:
                "Create standalone designs with clean edges or full-bleed artwork that covers the product completely.",

        },  

        // Define the style presets feature
        {
            // Give the feature a stable key
            key: "styles",

            // Define the simple icon
            icon: "✦",

            // Define the feature title
            title: "Choose the style",

            // Define the feature description
            description:
                "Use our preset options for a quick start or give complex prompts to match your own style.",
        },

        // Define the AI model feature
        {
            // Give the feature a stable key
            key: "models",

            // Define the simple icon
            icon: "⚙",

            // Define the feature title
            title: "Choose the model",

            // Define the feature description
            description:
                "Pick from models optimised for speed, quality or text handling to get the output you require.",

        },

        // Define the optimisation feature
        {
            // Give the feature a stable key
            key: "optimise",

            // Define the simple icon
            icon: "✓",

            // Define the feature title
            title: "Print-ready optimisation",

            // Define the feature description
            description:
                "Images are automatically prepared for high-quality print, upscaled and tidied without losing any detail.",

        },

        // Define the edit feature
        {
            // Give the feature a stable key
            key: "edit",

            // Define the simple icon
            icon: "✎",

            // Define the feature title
            title: "Edit without leaving StrichSuite",

            // Define the feature description
            description:
                "Fine-tune your generated artwork with built-in image editing tools without switching to separate design software.",

        },

        // Define the free feature
        {
            // Give the feature a stable key
            key: "free",

            // Define the simple icon
            icon: "£0",

            // Define the feature title
            title: "Completely free",

            // Define the feature description
            description:
                "Generate unlimited free images. No fees, credits or hidden costs.",

        },

    ];



    const AiDesignSection = ({
        imageSrc = "images/landing/ai_image_generation.png",
        imageAlt = "Creator generating artwork with AI",
    }) => {

        // Return the AI design section
        return (

            // Create the main AI design section
            <section className="ai-design">

                {/* Create the width-limited inner container */}
                <div className="ai-design__inner">

                    {/* Create the content area */}
                    <div className="ai-design__content">

                        {/* Display the main heading */}
                        <h2 className="ai-design__title">

                            {/* Display the heading text */}
                            Generate Designs with AI

                        </h2>

                        {/* Display the opening statement */}
                        <p className="ai-design__lead">

                            {/* Display the lead text */}
                            Turn an idea into artwork in seconds.

                        </p>

                        {/* Display the first paragraph */}
                        <p className="ai-design__text">

                            {/* Explain the AI design feature */}
                            Describe what you want to create and let StrichSuite generate original artwork for you. Specifically tailored for Print on Demand to create standalone designs with crisp edges or full-cover designs in the proportions you need. Designs are optimised for print quality, ready to sell with no restrictive licences, no royalties, and no other software or accounts needed. 

                        </p>

                        {/* Display the second paragraph 
                        <p className="ai-design__text">

                            {/* Explain who the feature is useful for 
                            Whether you already have a clear idea or just need a starting point, AI generation makes it easy to move from concept to product quickly.

                        </p>
                        */}
                        
                        {/* Display the closing statement */}
                        <p className="ai-design__highlight">

                            {/* Display the highlighted message */}
                            Dream it. Create it. Sell it.

                        </p>

                    </div>



                    {/* Create the image area */}
                    <div className="ai-design__image-wrapper">

                        {/* Display the AI design image */}
                        <img
                            className="ai-design__image"
                            src={imageSrc}
                            alt={imageAlt}
                        />

                    </div>

                </div>

                {/* Display the AI feature grid */}
                        <div className="ai-design__features">

                            {/* Loop through each AI feature */}
                            {aiFeatures.map((feature) => {

                                // Return the individual feature card
                                return (

                                    // Create the feature card
                                    <article
                                        key={feature.key}
                                        className="ai-design__feature"
                                    >

                                        {/* Display the feature icon */}
                                        <div
                                            className="ai-design__feature-icon"
                                            aria-hidden="true"
                                        >

                                            {/* Display the icon */}
                                            {feature.icon}

                                        </div>

                                        {/* Create the feature content */}
                                        <div className="ai-design__feature-content">

                                            {/* Display the feature title */}
                                            <h3 className="ai-design__feature-title">

                                                {/* Display the feature heading */}
                                                {feature.title}

                                            </h3>

                                            {/* Display the feature description */}
                                            <p className="ai-design__feature-description">

                                                {/* Display the feature copy */}
                                                {feature.description}

                                            </p>

                                        </div>

                                    </article>

                                );

                            })}

                        </div>


            </section>

        );

    };

    

    const ApiIntegrationSection = ({
        imageAlt = "Business owner managing their ecommerce store",
    }) => {

        // Return the API integration section
        return (

            // Create the main API integration section
            <section className="api-integration" id="api-integration">

                {/* Create the width-limited inner container */}
                <div className="api-integration__inner">

                    {/* Create the image area */}
                    <div className="api-integration__image-wrapper">

                        {/* Display the API integration image */}
                        <img
                            className="api-integration__image"
                            src="images/landing/api.png"
                            alt={imageAlt}
                        />

                    </div>

                    {/* Create the content area */}
                    <div className="api-integration__content">

                        {/* Display the main heading */}
                        <h2 className="api-integration__title">

                            {/* Display the heading text */}
                            API Integration

                        </h2>

                        {/* Display the first paragraph */}
                        <p className="api-integration__text">

                            {/* Explain the API use case */}
                            If you've already built your own store, checkout or ecommerce system, you don't need to change how you sell. Connect your platform to StrichSuite and use our print-on-demand infrastructure to fulfil your orders behind the scenes.

                        </p>

                        {/* Display the second paragraph */}
                        <p className="api-integration__text">

                            {/* Explain the fulfilment process */}
                            Send orders automatically for production, keep track of their progress and retrieve shipping and tracking information — all while your customers continue to buy through your existing platform.

                        </p>
                       

                        {/* Display the key message */}
                        <p className="api-integration__highlight">

                            {/* Display the closing statement */}
                            Your store. Your checkout. Your customer experience. Our fulfilment.

                        </p>

                    </div>

                </div>

            </section>

        );

    };



    const StartBuilding = ({
        createAccountUrl,
    }) => {

        // Render the final call-to-action section.
        return (

            // Render the outer section.
            <section
                className="start-building"
                aria-labelledby="start-building-title"
            >

                {/* Constrain and centre the section content. */}
                <div className="start-building__inner">

                    {/* Render the main call-to-action heading. */}
                    <h2
                        className="start-building__title"
                        id="start-building-title"
                    >
                        Start Selling Today
                    </h2>

                    {/* Render the main supporting message. */}
                    <p className="start-building__description">
                        Whether you're launching your first product or expanding an established brand, StrichSuite gives you everything you need to turn your ideas into products people love.
                    </p>

                    {/* Render the short closing message. */}
                    <p className="start-building__strapline">
                        Everything you need to create, sell and ship in one platform.
                    </p>

                    {/* Link directly to account registration. */}
                    <a
                        className="start-building__button"
                        href={createAccountUrl}
                    >
                        Create a Free Account
                    </a>

                </div>

            </section>

        );

    };




    // Render the complete landing page.
    return (

        // Wrap the complete landing page.
        <div className="landing-page">

            {/* Render the site's top navigation. */}
            <LandingHeader
                logo={{
                    // Set the existing StrichSuite logo image.
                    src: "/images/StrichSuite-logo-tagline-short.png",

                    // Provide the logo alternative text.
                    alt: "StrichSuite",
                }}
                signInUrl={landingLinks.signIn}
                createAccountUrl={landingLinks.createAccount}
            />


            {/* Render the permanent StrichSuite strapline. */}
            <div className="landing-page__strapline">

                {/* Constrain the strapline to the main site width. */}
                <div className="landing-page__strapline-inner">

                    {/* Describe the core StrichSuite offering. */}
                    <p className="landing-page__strapline-text">

                        {/* Describe the service. */}
                        Print on demand,

                        {/* Emphasise the main benefit. */}
                        <strong className="landing-page__strapline-highlight">
                            {" "}made easy.
                        </strong>

                    </p>

                </div>

            </div>


            {/* Render the main page content. */}
            <main className="landing-page__main">

                {/* Render the primary marketing carousel. */}
                <HeroCarousel
                    slides={landingSlides}
                    createAccountUrl={landingLinks.createAccount}
                    selectJourney={selectJourney}
                />

                {/* Render the dynamic How It Works section below the hero */}
                <HowItWorks
                    sectionRef={howItWorksRef}
                    activeJourney={activeJourney}
                    onJourneyChange={selectJourney}
                    journeys={howItWorksJourneys}
                />

                {/* Explain what happens after an order is placed. */}
                <SaleProcess
                    saleProcess={saleProcess}
                />

                {/* Render the api integration */}
                <AiDesignSection />

                {/* Render the api integration */}
                <ApiIntegrationSection />

                {/* Render the FAQ section further down the page. */}
                <FaqSection
                    faqs={landingFaqs}
                />

                {/* Render the final account creation call to action. */}
                <StartBuilding
                    createAccountUrl={landingLinks.createAccount}
                />

            </main>



            {/* Render the global site footer. */}
            <LandingFooter />


        </div>

    );

}; export default LandingPage;